import type { ReactNode } from 'react';
import type { IGitHubSyncStatus } from '@skystone/shared';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';

interface GitHubSyncStatusCardProps {
  title: string;
  description: string;
  status: IGitHubSyncStatus | null;
  isRunning: boolean;
  disabled?: boolean;
  onRun: () => void;
  resultContent?: ReactNode;
}

function formatRunAt(iso: string | null): string {
  if (iso === null) return 'Never';
  return new Date(iso).toLocaleString();
}

function StatusBadge({
  status,
}: Readonly<{ status: IGitHubSyncStatus['lastRunStatus'] | null }>) {
  if (status === 'success') return <Badge variant="success">Success</Badge>;
  if (status === 'failure') return <Badge variant="error">Failure</Badge>;
  return <Badge variant="secondary">Not yet run</Badge>;
}

export function GitHubSyncStatusCard({
  title,
  description,
  status,
  isRunning,
  disabled,
  onRun,
  resultContent,
}: Readonly<GitHubSyncStatusCardProps>) {
  const lastRunStatus = status?.lastRunStatus ?? null;
  const lastRunAt = status?.lastRunAt ?? null;
  const scheduleInterval = status?.scheduleInterval ?? 'daily';

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle className="text-base">{title}</CardTitle>
          <StatusBadge status={lastRunStatus} />
        </div>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <dl className="grid grid-cols-[max-content_1fr] gap-x-3 gap-y-1 text-sm">
          <dt className="text-muted-foreground">Last run</dt>
          <dd className="font-medium">{formatRunAt(lastRunAt)}</dd>
          <dt className="text-muted-foreground">Schedule</dt>
          <dd className="font-medium capitalize">{scheduleInterval}</dd>
        </dl>

        <div>
          <Button onClick={onRun} isLoading={isRunning} disabled={disabled ?? isRunning}>
            Run sync now
          </Button>
        </div>

        {resultContent}
      </CardContent>
    </Card>
  );
}
